import { lazy, Suspense } from "react";
import { useRoutes, Navigate } from "react-router-dom";
import SuspenseLoader from "@components/Loader/SuspenseLoader";
import Layout from "@layouts/index";
import RequireAuth from "@middlewares/RequireAuth";
import { UserRoles } from "@enums/UserRoles";
// import CampaignHeader from "@pages/public/donate/components/CampaignHeader";
import { AppRouteObject } from "@interfaces/Routes";
import { BreadcrumbItems } from "@constants/breadcrumbItems";

const Loader = (Component: any) => (props: any) => (
    <Suspense fallback={<SuspenseLoader />}>
        <Component {...props} />
    </Suspense>
);

const Login = Loader(lazy(() => import("@pages/public/login")));
const Register = Loader(lazy(() => import("@pages/public/register")));
const VerifyEmail = Loader(lazy(() => import("@pages/public/verifyEmail")));
const ForgotPassword = Loader(lazy(() => import("@pages/public/forgotPassword")));
const ResetPassword = Loader(lazy(() => import("@pages/public/resetPassword")));
const UserDashboard = Loader(lazy(() => import("@pages/user/dashboard")));
const AdminDashboard = Loader(lazy(() => import("@pages/admin/dashboard")));
const UserDonationHistory = Loader(lazy(() => import("@pages/user/donationHistory")));
const AdminDonationHistory = Loader(lazy(() => import("@pages/admin/donationHistory")));
const Campaign = Loader(lazy(() => import("@pages/admin/campaign")));
const DonorManagement = Loader(lazy(() => import("@pages/admin/donorManagement")));
const DonorDetails = Loader(lazy(() => import("@pages/admin/donorDetails")));
const DonateCampaign = Loader(lazy(() => import("@pages/public/donate")));
const CreateOrEditCampaign = Loader(lazy(() => import("@pages/admin/createOrEditCampaign")));
const Profile = Loader(lazy(() => import("@pages/account/profile")));
const Settings = Loader(lazy(() => import("@pages/account/settings")));
const PersonalSettings = Loader(lazy(() => import("@pages/account/settings/components/PersonalSettings")));
const AdminReport = Loader(lazy(() => import("@pages/admin/report")));
const UserReport = Loader(lazy(() => import("@pages/user/report")));
const CreateOrEditReport = Loader(lazy(() => import("@pages/admin/createOrEditReport")));
const Support = Loader(lazy(() => import("@pages/account/support")));
const Status404 = Loader(lazy(() => import("@pages/public/error")));
const ComingSoon = Loader(lazy(() => import("@pages/public/comingSoon")));
const OfflinePaymentManagement = Loader(lazy(() => import("@pages/admin/offlinePaymentManagement")));
const RefundRequestManagement = Loader(lazy(() => import("@pages/admin/refundRequest")));
const PatientManagement = Loader(lazy(() => import("@pages/admin/patient")));
const CreateOrEditPatient = Loader(lazy(() => import("@pages/admin/createOrEditPatient")));
const DeservingItemManagement = Loader(lazy(() => import("@pages/admin/deservingItem")));
const CreateOrEditDeservingItem = Loader(lazy(() => import("@pages/admin/createOrEditDeservingItem")));
const UserManagement = Loader(lazy(() => import("@pages/admin/userManagement")));
const UserDetails = Loader(lazy(() => import("@pages/admin/userDetails")));
const CreateOrEditUser = Loader(lazy(() => import("@pages/admin/createOrEditUser")));
const CreateOrEditUserForm = Loader(lazy(() => import("@pages/admin/createOrEditUser/createOrEditUserForm")));
const isServer = typeof window === 'undefined';

const nonLoggedInStateRoutes: AppRouteObject[] = [
    {
        path: "",
        element: <RequireAuth nonLoggedInPages={true} />,
        children: [
            {
                path: "login",
                element: <Login />,
            },
            {
                path: "register",
                element: <Register />,
            },
            {
                path: "verify-email/:token",
                element: <VerifyEmail />,
            },
            {
                path: "forgot-password",
                element: <ForgotPassword />,
            },
            {
                path: "reset-password/:token",
                element: <ResetPassword />,
            },
        ],
    },
];

const allStatesRoutes: AppRouteObject[] = [
    // {
    //     path: "",
    //     element: <CampaignHeader />,
    //     children: [
    //         {
    //             path: "campaign/:slug",
    //             element: <DonateCampaign />,
    //         },
    //     ],
    // },
    {
        path: "public-campaigns",
        element: <ComingSoon />,
    },
    {
        path: "about-us",
        element: <ComingSoon />,
    },
    {
        path: "about-thalassemia",
        element: <ComingSoon />,
    },
    {
        path: "terms-and-conditions",
        element: <ComingSoon />,
    },
    {
        path: "privacy-policy",
        element: <ComingSoon />,
    },
    {
        path: "contact",
        element: <ComingSoon />,
    },
    {
        path: "email",
        element: <ComingSoon />,
    },
    // {
    //     path: "*",
    //     element: <Status404 />,
    // },
];

const loggedInStateRoutes: AppRouteObject[] = [
    {
        path: "",
        element: <RequireAuth loggedInPages={true} allowedRoles={[UserRoles.USER, UserRoles.ADMIN, UserRoles.CCA, UserRoles.MA]} />,
        children: [
            {
                path: "",
                element: <Layout />,
                children: [
                    {
                        path: "user/dashboard",
                        element: <UserDashboard />,
                        breadcrumbs: [BreadcrumbItems.DASHBOARD_USER],
                    },
                    {
                        path: "user/donation-history",
                        element: <UserDonationHistory />,
                        breadcrumbs: [BreadcrumbItems.DASHBOARD_USER, BreadcrumbItems.DONATION_HISTORY_USER],
                    },
                    {
                        path: "user/reports",
                        element: <UserReport />,
                        breadcrumbs: [BreadcrumbItems.DASHBOARD_USER, BreadcrumbItems.USER_REPORTS],
                    },
                    {
                        path: "profile",
                        element: <Profile />,
                        breadcrumbs: [BreadcrumbItems.DASHBOARD_USER, BreadcrumbItems.PROFILE],
                    },
                    {
                        path: "settings",
                        element: <Settings />,
                        children: [
                            {
                                path: "personal",
                                element: <PersonalSettings />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_USER, BreadcrumbItems.PROFILE_SETTINGS],
                            },
                            {
                                path: "account",
                                element: <ComingSoon />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_USER, BreadcrumbItems.ACCOUNT_SETTINGS],
                            },
                        ],
                    },
                    {
                        path: "support",
                        element: <Support />,
                        breadcrumbs: [BreadcrumbItems.DASHBOARD_USER, BreadcrumbItems.SUPPORT],
                    },
                    {
                        path: "",
                        element: <RequireAuth loggedInPages={true} allowedRoles={[UserRoles.ADMIN]} />,
                        children: [
                            {
                                path: "admin/dashboard",
                                element: <AdminDashboard />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN],
                            },
                            {
                                path: "admin/donors",
                                element: <DonorManagement />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.DONORS],
                            },
                            {
                                path: "admin/donors/:id",
                                element: <DonorDetails />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.DONORS, BreadcrumbItems.DONOR_DETAILS],
                            },
                            {
                                path: "admin/users",
                                element: <UserManagement />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.USERS],
                            },
                            {
                                path: "admin/users/:id",
                                element: <UserDetails />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.USERS, BreadcrumbItems.USER_DETAILS],
                            },
                            {
                                path: "admin/users/create",
                                element: <CreateOrEditUserForm />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.USERS, BreadcrumbItems.USER_CREATE],
                            },
                            {
                                path: "admin/users/:id/edit",
                                element: <CreateOrEditUser />,
                                children: [
                                    {
                                        path: "profile",
                                        element: <CreateOrEditUserForm />,
                                        breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.USERS, BreadcrumbItems.USER_EDIT],
                                    },
                                    {
                                        path: "account",
                                        element: <ComingSoon />,
                                        breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.USERS, BreadcrumbItems.USER_EDIT],
                                    },
                                ],
                            },
                            {
                                path: "admin/offline-payments",
                                element: <OfflinePaymentManagement />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.OFFLINE_PAYMENTS],
                            },
                            {
                                path: "admin/donation-history",
                                element: <AdminDonationHistory />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.DONATION_HISTORY_ADMIN],
                            },
                            {
                                path: "admin/campaign",
                                element: <Campaign />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.CAMPAIGNS],
                            },
                            {
                                path: "admin/campaign/create",
                                element: <CreateOrEditCampaign />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.CAMPAIGNS, BreadcrumbItems.CAMPAIGN_CREATE],
                            },
                            {
                                path: "admin/campaign/edit/:slug",
                                element: <CreateOrEditCampaign />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.CAMPAIGNS, BreadcrumbItems.CAMPAIGN_EDIT],
                            },
                            {
                                path: "admin/reports",
                                element: <AdminReport />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.ADMIN_REPORTS],
                            },
                            {
                                path: "admin/reports/create",
                                element: <CreateOrEditReport />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.ADMIN_REPORTS, BreadcrumbItems.REPORT_CREATE],
                            },
                            {
                                path: "admin/reports/:id/edit",
                                element: <CreateOrEditReport />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.ADMIN_REPORTS, BreadcrumbItems.REPORT_EDIT],
                            },
                            {
                                path: "admin/refund-requests",
                                element: <RefundRequestManagement />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.REFUND_REQUEST],
                            },
                            {
                                path: "admin/patients",
                                element: <PatientManagement />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.PATIENTS],
                            },
                            {
                                path: "admin/patients/create",
                                element: <CreateOrEditPatient />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.PATIENTS, BreadcrumbItems.PATIENTS_CREATE],
                            },
                            {
                                path: "admin/patients/edit/:id",
                                element: <CreateOrEditPatient />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.PATIENTS, BreadcrumbItems.PATIENTS_EDIT],
                            },

                            {
                                path: "admin/deserving-items",
                                element: <DeservingItemManagement />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.DESERVING_ITEMS],
                            },
                            {
                                path: "admin/deserving-items/create",
                                element: <CreateOrEditDeservingItem />,
                                breadcrumbs: [
                                    BreadcrumbItems.DASHBOARD_ADMIN,
                                    BreadcrumbItems.DESERVING_ITEMS,
                                    BreadcrumbItems.DESERVING_ITEMS_CREATE,
                                ],
                            },
                            {
                                path: "admin/deserving-items/edit/:id",
                                element: <CreateOrEditDeservingItem />,
                                breadcrumbs: [BreadcrumbItems.DASHBOARD_ADMIN, BreadcrumbItems.DESERVING_ITEMS, BreadcrumbItems.DESERVING_ITEMS_EDIT],
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

export const routes: AppRouteObject[] = [
    { path: "", element:  !isServer ?<Navigate to={"login"} />:null },
    ...nonLoggedInStateRoutes,
    ...allStatesRoutes,
    ...loggedInStateRoutes,
];

const PageRoutes = () => {
    return useRoutes(routes);
};

export default PageRoutes;
